// Google Analytics utility functions

/**
 * Initialize Google Analytics (already done in index.html, but this ensures it's available)
 */
export const initGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    return true;
  }
  return false;
};

/**
 * Track page views
 * @param {string} path - The path of the page
 * @param {string} title - Optional page title
 */
export const trackPageView = (path, title = '') => {
  if (typeof window !== 'undefined' && window.gtag) {
    const pageTitle = title || document.title || path;
    const pageLocation = window.location.origin + path;
    
    // Update config with new page info and enable engagement tracking
    window.gtag('config', 'G-3X35Y1WY5Q', {
      page_path: path,
      page_title: pageTitle,
      page_location: pageLocation,
      // Enable engagement time tracking
      engagement_time_msec: 100,
    });
    
    // Send explicit page_view event for GA4 (this is what GA4 uses)
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: pageTitle,
      page_location: pageLocation,
    });
    
    // Debug logging (can be removed later)
    if (process.env.NODE_ENV === 'development') {
      console.log('GA Page View:', { path, title: pageTitle, location: pageLocation });
    }
  } else if (typeof window !== 'undefined') {
    // If gtag isn't ready yet, wait a bit and try again
    setTimeout(() => trackPageView(path, title), 100);
  }
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track button clicks
 * @param {string} buttonName - Name/identifier of the button
 * @param {string} location - Where the button is located (component/page name)
 * @param {object} additionalParams - Any additional parameters
 */
export const trackButtonClick = (buttonName, location = '', additionalParams = {}) => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: location,
    ...additionalParams,
  });
};

/**
 * Track navigation clicks
 * @param {string} linkText - Text of the navigation link
 * @param {string} destination - Destination path
 */
export const trackNavigation = (linkText, destination) => {
  trackEvent('navigation_click', {
    link_text: linkText,
    destination: destination,
  });
};

/**
 * Track video interactions
 * @param {string} action - Action type (play, pause, close, etc.)
 * @param {string} videoTitle - Title/identifier of the video
 * @param {string} location - Where the video is located
 */
export const trackVideoInteraction = (action, videoTitle = '', location = '') => {
  trackEvent('video_interaction', {
    action: action,
    video_title: videoTitle,
    video_location: location,
  });
};

/**
 * Track modal interactions
 * @param {string} action - Action type (open, close)
 * @param {string} modalName - Name/identifier of the modal
 */
export const trackModal = (action, modalName = '') => {
  trackEvent('modal_interaction', {
    action: action,
    modal_name: modalName,
  });
};

/**
 * Track external link clicks
 * @param {string} url - The external URL
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (url, linkText = '') => {
  trackEvent('external_link_click', {
    link_url: url,
    link_text: linkText,
  });
};

/**
 * Track form submissions
 * @param {string} formName - Name/identifier of the form
 */
export const trackFormSubmit = (formName = '') => {
  trackEvent('form_submit', {
    form_name: formName,
  });
};

/**
 * Track tab/accordion interactions
 * @param {string} tabName - Name of the tab
 * @param {string} action - Action type (open, close, switch)
 */
export const trackTabInteraction = (tabName, action = 'switch') => {
  trackEvent('tab_interaction', {
    tab_name: tabName,
    action: action,
  });
};
